/**
* @render react
* @name List
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqid from 'uniqid';

import { withPolaris } from 'Components/protons/Polaris';

import { ListLayout } from 'Components/molecules/ListLayout';

/**
 * List component
 *
 * @param {string} title - Title for the list
 * @param {array} content - Array containing objects with the following keys: url, title, subtitle, excerpt, image
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {string} component - Component to be passed to ListLayout to render the child
 */
const List = (
  {
    title,
    content,
    loadStyles,
    showIndex,
    extraClassNames,
    context: { cssPrefix },
    component,
  },
) => (
  <>
    {title && (
      <p className={`${cssPrefix}__list--title`}>{title}</p>
    )}

    <ul
      className={classNames({
        [`${cssPrefix}__list`]: loadStyles,
        ...extraClassNames,
      })}
    >
      {content.map((singleContent, index) => (
        <ListLayout
          showIndex={showIndex}
          index={index + 1}
          key={uniqid(`list-layout-${index}`)}
          {...singleContent}
          component={component}
        />
      ))}
    </ul>
  </>
);

/**
 * Valid props
 */
List.propTypes = {
  title: PropTypes.string,
  content: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    subtitle: PropTypes.string,
    excerpt: PropTypes.string,
    image: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
  })),
  loadStyles: PropTypes.bool,
  showIndex: PropTypes.bool,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  component: PropTypes.string,
};

/**
 * Default props
 */
List.defaultProps = {
  title: '',
  content: [],
  loadStyles: true,
  showIndex: false,
  extraClassNames: {},
  context: {},
  component: '',
};

export default withPolaris(List);
